import { useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Compass, Info, LogOut, Undo2 } from "lucide-react";
import useUser from "@app/zustand/user";
import { signOut } from "@app/utils/sign-out";
import { hasDemoReturn, switchDemoSession } from "@app/utils/demo-account";
import { refreshUser } from "@app/utils/use-most-fresh-user";
import { usePreviewView } from "@app/zustand/preview-view";
import { useScope } from "@app/zustand/scope";
import { useDemoTour } from "@app/components/demo-tour/demo-tour-store";

export default function DemoBanner() {
  const navigate = useNavigate();
  const { t } = useLingui();
  const user = useUser((state) => state.user);
  const setUser = useUser((state) => state.setUser);
  // A user who entered from the header goes back to their account rather than signing out.
  const demoReturn = hasDemoReturn(user);
  const [leaving, setLeaving] = useState(false);
  const previewRole = usePreviewView((state) => state.previewRole);
  const setPreview = usePreviewView((state) => state.setPreview);
  const setScope = useScope((state) => state.setScope);
  const startTour = useDemoTour((state) => state.start);

  const handleExit = () =>
    signOut(() => {
      setUser(null);
      navigate("/welcome");
    });

  const handleReturn = async () => {
    setLeaving(true);
    if (await switchDemoSession("leave")) return;
    setLeaving(false);
    toast.error(t`Impossible de revenir à votre compte.`);
    // The refused return cookie is gone: the button goes with it.
    void refreshUser("demo-leave");
  };

  const isGlobalView = previewRole === "global";

  // The demo's persona toggle also drives the data perimeter: "Vue globale" IS
  // the anonymised speciality view (scope=specialite), while "Vue médecin" is the
  // nominative own-patientèle view (scope=mine).
  const handleSwitchToMedecin = () => {
    setPreview({ role: "medecin" });
    setScope("mine");
    navigate("/dashboard");
  };

  const handleSwitchToGlobal = () => {
    setPreview({ role: "global" });
    setScope("specialite");
    navigate("/dashboard");
  };

  return (
    <div className="flex items-center justify-between gap-3 bg-primary/10 border-b border-primary/20 px-4 py-2 text-sm text-primary">
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center gap-1.5 font-medium">
          <Info className="h-[15px] w-[15px] shrink-0" aria-hidden />
          <Trans>Mode démo</Trans>
        </span>
        <span className="text-slate-500">—</span>
        <span className="text-slate-600">
          <Trans>Données synthétiques, aucun patient réel</Trans>
        </span>

        {/* View mode toggle: médecin vs globale */}
        <div
          data-tour="demo-persona"
          className="flex items-center gap-1 rounded-full bg-white/80 border border-primary/20 p-0.5 ml-2"
        >
          <button
            onClick={handleSwitchToMedecin}
            className={`rounded-full px-3 py-0.5 text-xs font-medium transition-colors ${
              !isGlobalView
                ? "bg-primary text-white shadow-sm"
                : "text-slate-500 hover:text-primary"
            }`}
          >
            <Trans>Vue médecin</Trans>
          </button>
          <button
            onClick={handleSwitchToGlobal}
            className={`rounded-full px-3 py-0.5 text-xs font-medium transition-colors ${
              isGlobalView
                ? "bg-primary text-white shadow-sm"
                : "text-slate-500 hover:text-primary"
            }`}
          >
            <Trans>Vue globale</Trans>
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          data-tour="demo-tour"
          onClick={startTour}
          className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-0.5 text-xs font-medium text-white shadow-sm hover:bg-primary/90 transition-colors"
        >
          <Compass className="h-3 w-3" aria-hidden />
          <Trans>Visite guidée</Trans>
        </button>
        {demoReturn ? (
          <button
            onClick={handleReturn}
            disabled={leaving}
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white/70 px-3 py-0.5 text-xs font-medium text-primary hover:bg-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Undo2 className="h-[11px] w-[11px]" aria-hidden />
            <Trans>Revenir à mon compte</Trans>
          </button>
        ) : (
          <button
            onClick={handleExit}
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white/70 px-3 py-0.5 text-xs font-medium text-primary hover:bg-white transition-colors"
          >
            <LogOut className="h-[11px] w-[11px]" aria-hidden />
            <Trans>Quitter la démo</Trans>
          </button>
        )}
      </div>
    </div>
  );
}
